import { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  IconButton,
} from "@mui/material";
import {
  ArrowBack,
  FileDownload,
  Language,
  Email,
  Phone,
  LocationOn,
  OpenInNew,
} from "@mui/icons-material";
import { api } from "../api";
import { useAllianceDetail } from "../hooks";
import { downloadJsonFile } from "../utils/downloadAllianceJson";
import {
  ABOUT_CONTENT_MAX_LENGTH,
  ALLIANCE_DETAIL_LIST_PAGE_SIZE,
} from "../constants";
import {
  ClientSidePagination,
  useClientSidePagination,
} from "../components/ClientSidePagination";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { SectionPaper } from "../components/SectionPaper";
import { LinkList, type LinkItem } from "../components/LinkList";
import { IconDetail } from "../components/IconDetail";
import {
  NewsPreviewDialog,
  type NewsArticlePreview,
} from "../components/NewsPreviewDialog";
import {
  formatPhoneForTel,
  generateGoogleMapsSearchUrl,
  splitPhoneParts,
  hasNonEmpty,
  hasPhoneContact,
} from "../utils/contactLinks";
import {
  buildAllianceQuickLinks,
  formatFullAddress,
  normalizeHttpUrl,
} from "../utils/allianceQuickLinks";
import { AllianceQuickLinkIcons } from "../components/AllianceQuickLinkIcons";
import { otherEntriesForTable } from "../utils/otherInfo";
import { stripTrailingNewsEllipsis } from "../utils/newsFormat";
import { SmartInlineText } from "../components/SmartInlineText";
import { resolveBackToSearch } from "../utils/allianceNavigation";

export function AllianceDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { alliance, loading } = useAllianceDetail();
  const [previewNews, setPreviewNews] = useState<NewsArticlePreview | null>(
    null,
  );
  const [jsonExportBusy, setJsonExportBusy] = useState(false);
  const [jsonExportError, setJsonExportError] = useState<string | null>(null);
  const handleExportAllianceJson = useCallback(async () => {
    if (!id) return;
    setJsonExportError(null);
    setJsonExportBusy(true);
    try {
      const full = await api.getAllianceById(id);
      downloadJsonFile(full, id);
    } catch {
      setJsonExportError(
        "Izvoz nije uspio. Provjerite vezu ili pokušajte ponovo.",
      );
    } finally {
      setJsonExportBusy(false);
    }
  }, [id]);

  const newsList = Array.isArray(alliance?.news) ? alliance.news : [];
  const membersList = Array.isArray(alliance?.members) ? alliance.members : [];
  const documentsList: LinkItem[] = Array.isArray(alliance?.documents)
    ? (alliance.documents as LinkItem[])
    : [];
  const referencesList: LinkItem[] = Array.isArray(alliance?.references)
    ? (alliance.references as LinkItem[])
    : [];

  const newsPagination = useClientSidePagination(
    newsList,
    ALLIANCE_DETAIL_LIST_PAGE_SIZE,
    id,
  );
  const membersPagination = useClientSidePagination(
    membersList,
    ALLIANCE_DETAIL_LIST_PAGE_SIZE,
    id,
  );
  const documentsPagination = useClientSidePagination(
    documentsList,
    ALLIANCE_DETAIL_LIST_PAGE_SIZE,
    id,
  );
  const referencesPagination = useClientSidePagination(
    referencesList,
    ALLIANCE_DETAIL_LIST_PAGE_SIZE,
    id,
  );

  if (loading || !alliance) {
    return <LoadingSpinner />;
  }

  const { details, address, contact, other, rulebooks, about, schedules } =
    alliance;

  const otherRows = otherEntriesForTable(other as Record<string, unknown>);
  const aboutItems = (about ?? []).filter(
    (a: { link?: string; content?: string }) =>
      hasNonEmpty(a.link) || hasNonEmpty(a.content),
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(resolveBackToSearch(location))}
          sx={{ flexShrink: 0 }}
        >
          Natrag na pretragu
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 8 } }} />
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<FileDownload />}
          disabled={!id || jsonExportBusy}
          onClick={() => void handleExportAllianceJson()}
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            minWidth: { sm: 220 },
          }}
        >
          Izvezi cijeli zapis (JSON)
        </Button>
      </Box>
      {jsonExportError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }} role="alert">
          {jsonExportError}
        </Typography>
      )}

      <AllianceHeader alliance={alliance} />

      {otherRows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              fontWeight: 600,
            }}
          >
            Ostale informacije
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {otherRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        width: "38%",
                        verticalAlign: "top",
                        borderColor: "divider",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        verticalAlign: "top",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        borderColor: "divider",
                      }}
                    >
                      <SmartInlineText text={row.value} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {newsList.length > 0 && (
        <SectionPaper title="Vijesti" count={newsList.length}>
          <ArticlesList
            articles={newsPagination.slice}
            onOpenPreview={(article) => setPreviewNews(article)}
            keyOffset={newsPagination.firstIndex}
          />
          <ClientSidePagination
            page={newsPagination.page}
            pageSize={newsPagination.pageSize}
            total={newsPagination.total}
            onPageChange={newsPagination.setPage}
            aria-label="Stranice vijesti"
          />
        </SectionPaper>
      )}

      <NewsPreviewDialog
        open={!!previewNews}
        article={previewNews}
        onClose={() => setPreviewNews(null)}
      />

      {membersList.length > 0 && (
        <SectionPaper title="Članovi" count={membersList.length}>
          <MembersTable
            members={membersPagination.slice}
            keyOffset={membersPagination.firstIndex}
          />
          <ClientSidePagination
            page={membersPagination.page}
            pageSize={membersPagination.pageSize}
            total={membersPagination.total}
            onPageChange={membersPagination.setPage}
            aria-label="Stranice članova"
          />
        </SectionPaper>
      )}

      {documentsList.length > 0 && (
        <SectionPaper title="Dokumenti" count={documentsList.length}>
          <LinkList
            items={documentsPagination.slice}
            firstIndex={documentsPagination.firstIndex}
          />
          <ClientSidePagination
            page={documentsPagination.page}
            pageSize={documentsPagination.pageSize}
            total={documentsPagination.total}
            onPageChange={documentsPagination.setPage}
            aria-label="Stranice dokumenata"
          />
        </SectionPaper>
      )}

      {rulebooks && rulebooks.length > 0 && (
        <SectionPaper title="Pravilnici" count={rulebooks.length}>
          <LinkList items={rulebooks} />
        </SectionPaper>
      )}

      {referencesList.length > 0 && (
        <SectionPaper title="Reference" count={referencesList.length}>
          <LinkList
            items={referencesPagination.slice}
            firstIndex={referencesPagination.firstIndex}
          />
          <ClientSidePagination
            page={referencesPagination.page}
            pageSize={referencesPagination.pageSize}
            total={referencesPagination.total}
            onPageChange={referencesPagination.setPage}
            aria-label="Stranice referenci"
          />
        </SectionPaper>
      )}

      {schedules && schedules.length > 0 && (
        <SectionPaper title="Raspored" count={schedules.length}>
          <SchedulesList schedules={schedules} />
        </SectionPaper>
      )}

      {aboutItems.length > 0 && (
        <SectionPaper title="O savezu">
          {aboutItems.map((a: any, i: number) => (
            <Box key={i}>
              {a.link && (
                <Link
                  href={normalizeHttpUrl(String(a.link))}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ display: "block", mb: 1 }}
                >
                  {a.link}
                </Link>
              )}
              {a.content && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {a.content.substring(0, ABOUT_CONTENT_MAX_LENGTH)}
                </Typography>
              )}
            </Box>
          ))}
        </SectionPaper>
      )}
    </Box>
  );
}

function PhoneLines({ label, value }: { label: string; value: string }) {
  const parts = splitPhoneParts(value);
  if (parts.length === 0) return null;
  const tel0 = formatPhoneForTel(parts[0]);
  return (
    <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
      {label}:{" "}
      {tel0 ? (
        <Link href={`tel:${tel0}`} color="inherit" underline="hover">
          {parts[0]}
        </Link>
      ) : (
        parts[0]
      )}
      {parts.length > 1 ? `, ${parts.slice(1).join(", ")}` : null}
    </Typography>
  );
}

function MailLines({ mail }: { mail: string }) {
  const parts = mail
    .split(/[,;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return <Typography variant="body2">{mail}</Typography>;
  }
  const [first, ...rest] = parts;
  return (
    <Typography variant="body2" component="div">
      <Link href={`mailto:${first}`} color="inherit" underline="hover">
        {first}
      </Link>
      {rest.length > 0 ? `, ${rest.join(", ")}` : null}
    </Typography>
  );
}

function AllianceHeader({ alliance }: { alliance: any }) {
  const details = alliance.details;
  const address = alliance.address;
  const contact = alliance.contact;
  const formattedAddress = formatFullAddress(address);
  const showPhoneContact = hasPhoneContact(contact);
  const showMail = hasNonEmpty(contact?.mail);

  const mainUrl =
    details?.link != null && String(details.link).trim() !== ""
      ? String(details.link).trim()
      : "";
  const quickLinkCount = buildAllianceQuickLinks(alliance).length;
  const showUrlAndQuickRow = Boolean(mainUrl) || quickLinkCount > 0;

  return (
    <Paper
      elevation={0}
      sx={{ p: 4, mb: 4, borderRadius: 2, border: 1, borderColor: "divider" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ flex: "1 1 0%", minWidth: 0 }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            {details?.name || "Nepoznat savez"}
          </Typography>

          {(details?.sport || showUrlAndQuickRow) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                mb: details?.description ? 2 : 0,
              }}
            >
              {details?.sport ? (
                <Chip
                  label={details.sport}
                  color="secondary"
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              ) : null}
              {mainUrl || quickLinkCount > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  {mainUrl ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        maxWidth: "100%",
                      }}
                    >
                      <Language
                        color="action"
                        fontSize="small"
                        sx={{ flexShrink: 0 }}
                      />
                      <Link
                        href={normalizeHttpUrl(mainUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {details.link}
                      </Link>
                    </Box>
                  ) : null}
                  {quickLinkCount > 0 ? (
                    <AllianceQuickLinkIcons alliance={alliance} />
                  ) : null}
                </Box>
              ) : null}
            </Box>
          )}

          {details?.description && (
            <Typography
              variant="body1"
              color="text.secondary"
              component="div"
              sx={{
                mt: details?.sport || showUrlAndQuickRow ? 0 : 2,
                maxWidth: 700,
              }}
            >
              <SmartInlineText text={details.description} />
            </Typography>
          )}
        </Box>
        {details?.logo && (
          <Box
            component="img"
            src={details.logo}
            alt={details?.name || ""}
            sx={{ maxWidth: 100, maxHeight: 100 }}
          />
        )}
      </Box>

      {(formattedAddress || showPhoneContact || showMail) && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {formattedAddress && (
            <Grid size={{ xs: 12, md: 4 }}>
              <IconDetail icon={<LocationOn color="action" />} label="Adresa">
                {(() => {
                  const mapsUrl = generateGoogleMapsSearchUrl(address);
                  const inner = (
                    <Typography variant="body2">{formattedAddress}</Typography>
                  );
                  return mapsUrl ? (
                    <Link
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      underline="hover"
                      sx={{ display: "block" }}
                    >
                      {inner}
                    </Link>
                  ) : (
                    inner
                  );
                })()}
              </IconDetail>
            </Grid>
          )}
          {showPhoneContact && (
            <Grid size={{ xs: 12, md: 4 }}>
              <IconDetail icon={<Phone color="action" />} label="Kontakt">
                {hasNonEmpty(contact.phone) && (
                  <PhoneLines label="Tel" value={contact.phone} />
                )}
                {hasNonEmpty(contact.mobile) && (
                  <PhoneLines label="Mob" value={contact.mobile} />
                )}
                {hasNonEmpty(contact.fax) && (
                  <PhoneLines label="Faks" value={contact.fax} />
                )}
              </IconDetail>
            </Grid>
          )}
          {showMail && (
            <Grid size={{ xs: 12, md: 4 }}>
              <IconDetail icon={<Email color="action" />} label="E-pošta">
                <MailLines mail={contact.mail} />
              </IconDetail>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  );
}

function ArticlesList({
  articles,
  onOpenPreview,
  keyOffset = 0,
}: {
  articles: any[];
  onOpenPreview: (article: NewsArticlePreview) => void;
  keyOffset?: number;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {articles.map((article: any, i: number) => {
        const rawTitle = article.title != null ? String(article.title) : "";
        const titleShown =
          stripTrailingNewsEllipsis(rawTitle) ||
          (article.link ? String(article.link) : "") ||
          "-";

        const hasPreviewBody = Boolean(
          (article.content && String(article.content).trim()) || article.lead,
        );

        const rowKey = `${keyOffset + i}-${article.link ?? ""}-${article.timestamp ?? ""}-${titleShown.slice(0, 48)}`;

        return (
          <Box
            key={rowKey}
            sx={{ pb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            {article.timestamp && (
              <Typography variant="caption" color="secondary.main">
                {article.timestamp}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                flexWrap: "wrap",
              }}
            >
              {!hasPreviewBody && article.link ? (
                <Typography
                  variant="body2"
                  component={Link}
                  href={normalizeHttpUrl(String(article.link))}
                  target="_blank"
                  rel="noopener noreferrer"
                  fontWeight={600}
                  sx={{ flex: "1 1 auto", minWidth: 0, color: "primary.main" }}
                >
                  {titleShown}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  component={hasPreviewBody ? "button" : "span"}
                  onClick={
                    hasPreviewBody ? () => onOpenPreview(article) : undefined
                  }
                  fontWeight={600}
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: hasPreviewBody ? "pointer" : "default",
                    color: hasPreviewBody ? "primary.main" : "text.primary",
                    font: "inherit",
                    "&:hover": hasPreviewBody
                      ? { textDecoration: "underline" }
                      : undefined,
                  }}
                >
                  {titleShown}
                </Typography>
              )}
              {article.link && hasPreviewBody && (
                <IconButton
                  component="a"
                  href={normalizeHttpUrl(String(article.link))}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Otvori izvorni članak"
                  size="small"
                  sx={{ mt: -0.5 }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              )}
            </Box>
            {article.lead && (
              <Typography
                variant="body2"
                color="text.secondary"
                component={hasPreviewBody ? "button" : "div"}
                onClick={
                  hasPreviewBody ? () => onOpenPreview(article) : undefined
                }
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "none",
                  padding: 0,
                  mt: 0.5,
                  cursor: hasPreviewBody ? "pointer" : "default",
                  font: "inherit",
                  "&:hover": hasPreviewBody
                    ? { textDecoration: "underline" }
                    : undefined,
                }}
              >
                {stripTrailingNewsEllipsis(String(article.lead))}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function MembersTable({
  members,
  keyOffset = 0,
}: {
  members: any[];
  keyOffset?: number;
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Naziv</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Adresa</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Kontakt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((m: any, i: number) => (
            <TableRow key={`${keyOffset + i}-${m.link ?? ""}-${m.name ?? ""}`}>
              <TableCell>
                {m.link ? (
                  <Link
                    href={normalizeHttpUrl(String(m.link))}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {m.name || m.link}
                  </Link>
                ) : (
                  m.name || "-"
                )}
              </TableCell>
              <TableCell>{m.address || "-"}</TableCell>
              <TableCell>{m.phone || m.mail || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SchedulesList({ schedules }: { schedules: any[] }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {schedules.map((s: any, i: number) => (
        <Box key={i} sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="body2" fontWeight={600}>
            {s.link ? (
              <Link
                href={normalizeHttpUrl(String(s.link))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {s.title || s.link}
              </Link>
            ) : (
              s.title || "-"
            )}
          </Typography>
          {s.timestamp && (
            <Typography variant="caption" color="text.secondary">
              {s.timestamp}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
